/**
 * The Security Center (/security): how Defera protects accounts, payments
 * and data, at a glance and then page by page, with the two ways to reach
 * us about security. Public, outside the sign-in wall, written by Silas
 * (SI, VP Security & Reliability). Every claim lives in
 * src/content/security/pages.js, where the guardrails check it.
 */
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, FileText } from 'lucide-react';
import { SECURITY_CENTER, SECURITY_TXT_PATH } from '@/content/security';
import { securityHomeMeta } from '@/content/security/seo';
import HelpShell from '@/components/help/HelpShell';
import HelpSeo from '@/components/help/HelpSeo';
import AuthorByline from '@/components/help/AuthorByline';
import SecurityIcon from '@/components/security/SecurityIcon';
import SecurityReportPanel, { PANEL } from '@/components/security/SecurityReportPanel';

const POLICIES = [
  { to: '/privacy', label: 'Privacy Policy', text: 'What we collect, how we use it and your rights over it.' },
  { to: '/terms', label: 'Terms of Service', text: 'The agreement that covers your account and your trips.' },
  { href: SECURITY_TXT_PATH, label: 'security.txt', text: 'Our security contact, in the standard format security tools read.' },
];

export default function SecurityHome() {
  const { pages, highlights, author, updated } = SECURITY_CENTER;

  return (
    <HelpShell showSearch={false} homeLabel="Security" homeHref="/security">
      <HelpSeo meta={securityHomeMeta(SECURITY_CENTER)} />

      <section className="px-4 sm:px-6 pt-16 sm:pt-24 pb-14">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[12px] uppercase tracking-[0.24em] text-[#AAB4D6]">Defera Security</p>
          <h1 className="mt-4 text-[40px] sm:text-[56px] leading-[1.05] font-semibold tracking-[-0.03em] text-white">
            Security at Defera
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-[16px] sm:text-[17px] leading-relaxed text-white/70">
            How we protect your account, your payments and your data, what we’ll never ask you for, and how to reach us about a security concern.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/security/responsible-disclosure" className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[14px] font-medium text-black hover:bg-white/90">
              Report a vulnerability <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <Link to="/security/avoiding-scams" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-[14px] font-medium text-white hover:bg-white/[0.06]">
              Spot a scam
            </Link>
          </div>
          <div className="mt-9 flex justify-center">
            <AuthorByline author={author} updated={updated} compact />
          </div>
        </div>
      </section>

      <div className="px-4 sm:px-6 pb-24">
        <div className="mx-auto max-w-6xl space-y-16">
          <section aria-labelledby="at-a-glance">
            <h2 id="at-a-glance" className="text-[22px] font-medium text-white">At a glance</h2>
            <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {highlights.map((h) => (
                <li key={h.title}>
                  <Link to={h.href} className={`${PANEL} group flex h-full flex-col px-5 py-5 transition-colors hover:border-white/20`}>
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06] ring-1 ring-white/10 text-[#C9D0EA]">
                      <SecurityIcon name={h.icon} />
                    </span>
                    <span className="mt-4 text-[16.5px] font-medium text-white">{h.title}</span>
                    <span className="mt-1.5 text-[14.5px] leading-relaxed text-white/65">{h.text}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section aria-labelledby="explore">
            <h2 id="explore" className="text-[22px] font-medium text-white">Explore security at Defera</h2>
            <ul className="mt-5 grid gap-x-10 gap-y-1 md:grid-cols-2">
              {pages.map((p) => (
                <li key={p.slug}>
                  <Link to={p.path} className="group flex items-start gap-4 border-b border-white/[0.07] py-4">
                    <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.05] ring-1 ring-white/10 text-[#C9D0EA]">
                      <SecurityIcon name={p.icon} className="h-[18px] w-[18px]" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-3 text-[16px] font-medium text-white/90 group-hover:text-white">
                        {p.title}
                        <ArrowUpRight className="h-4 w-4 flex-shrink-0 text-white/30 group-hover:text-white/70" aria-hidden />
                      </span>
                      <span className="mt-1 block text-[14px] leading-relaxed text-white/55">{p.summary}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section aria-label="Contact us about security">
            <SecurityReportPanel />
          </section>

          <section aria-labelledby="policies">
            <h2 id="policies" className="text-[22px] font-medium text-white">Policies</h2>
            <ul className="mt-5 grid gap-4 sm:grid-cols-3">
              {POLICIES.map((d) => {
                const inner = (
                  <>
                    <span className="flex items-center gap-2 text-[15.5px] font-medium text-white">
                      <FileText className="h-4 w-4 text-white/50" strokeWidth={1.5} aria-hidden />
                      {d.label}
                    </span>
                    <span className="mt-1.5 block text-[14px] leading-relaxed text-white/55">{d.text}</span>
                  </>
                );
                const cls = `${PANEL} block h-full px-5 py-4 transition-colors hover:border-white/20`;
                return (
                  <li key={d.label}>
                    {d.to ? <Link to={d.to} className={cls}>{inner}</Link> : <a href={d.href} className={cls}>{inner}</a>}
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      </div>
    </HelpShell>
  );
}
